import type { ExtractedMesh } from "./surfaceNets";

export interface Bounds {
  min: [number, number, number];
  max: [number, number, number];
  size: [number, number, number];
}

export function computeBounds(positions: Float32Array): Bounds {
  const min: [number, number, number] = [Infinity, Infinity, Infinity];
  const max: [number, number, number] = [-Infinity, -Infinity, -Infinity];
  for (let p = 0; p < positions.length; p += 3) {
    for (let a = 0; a < 3; a++) {
      const v = positions[p + a] as number;
      if (v < (min[a] as number)) min[a] = v;
      if (v > (max[a] as number)) max[a] = v;
    }
  }
  if (!Number.isFinite(min[0])) {
    return { min: [0, 0, 0], max: [0, 0, 0], size: [0, 0, 0] };
  }
  return { min, max, size: [max[0] - min[0], max[1] - min[1], max[2] - min[2]] };
}

/**
 * Merge vertices that share a position, then drop triangles that collapse as a
 * result. Removing a triangle with a repeated vertex is safe: its two
 * remaining edges are the same edge traversed both ways, so they cancel and
 * the surface stays closed.
 */
export function weldAndClean(mesh: ExtractedMesh, epsilon = 1e-5): ExtractedMesh {
  const { positions, indices } = mesh;
  const inv = 1 / epsilon;
  const map = new Map<string, number>();
  const remap = new Int32Array(positions.length / 3);
  const out: number[] = [];

  for (let v = 0; v < positions.length / 3; v++) {
    const x = positions[v * 3] as number;
    const y = positions[v * 3 + 1] as number;
    const z = positions[v * 3 + 2] as number;
    const key = `${Math.round(x * inv)},${Math.round(y * inv)},${Math.round(z * inv)}`;
    const found = map.get(key);
    if (found === undefined) {
      const id = out.length / 3;
      map.set(key, id);
      out.push(x, y, z);
      remap[v] = id;
    } else {
      remap[v] = found;
    }
  }

  const tris: number[] = [];
  for (let t = 0; t < indices.length; t += 3) {
    const a = remap[indices[t] as number] as number;
    const b = remap[indices[t + 1] as number] as number;
    const c = remap[indices[t + 2] as number] as number;
    if (a === b || b === c || a === c) continue;
    tris.push(a, b, c);
  }

  // Drop vertices that no triangle references any more.
  const used = new Int32Array(out.length / 3).fill(-1);
  for (const idx of tris) used[idx] = 0;
  const finalPositions: number[] = [];
  for (let v = 0; v < used.length; v++) {
    if (used[v] === 0) {
      used[v] = finalPositions.length / 3;
      finalPositions.push(out[v * 3] as number, out[v * 3 + 1] as number, out[v * 3 + 2] as number);
    }
  }
  const finalIndices = new Uint32Array(tris.length);
  for (let t = 0; t < tris.length; t++) finalIndices[t] = used[tris[t] as number] as number;

  return { positions: new Float32Array(finalPositions), indices: finalIndices };
}

export interface ManifoldReport {
  triangleCount: number;
  vertexCount: number;
  /** Edges used by only one triangle — a hole in the surface. */
  boundaryEdges: number;
  /** Edges used by more than two triangles, or twice in the same direction. */
  nonManifoldEdges: number;
  /** Triangles with no area. */
  degenerateTriangles: number;
  closed: boolean;
}

/**
 * Verify the property slicers actually care about: every edge is used exactly
 * once in each direction. That proves the surface is closed AND consistently
 * wound, which together mean a solid with outward normals.
 */
export function inspectMesh(mesh: ExtractedMesh, areaEpsilon = 1e-9): ManifoldReport {
  const { positions, indices } = mesh;
  const forward = new Map<number, number>();
  const vertexCount = positions.length / 3;
  const key = (a: number, b: number): number => a * vertexCount + b;

  let degenerate = 0;
  for (let t = 0; t < indices.length; t += 3) {
    const a = indices[t] as number;
    const b = indices[t + 1] as number;
    const c = indices[t + 2] as number;
    if (triangleArea(positions, a, b, c) <= areaEpsilon) degenerate++;
    forward.set(key(a, b), (forward.get(key(a, b)) ?? 0) + 1);
    forward.set(key(b, c), (forward.get(key(b, c)) ?? 0) + 1);
    forward.set(key(c, a), (forward.get(key(c, a)) ?? 0) + 1);
  }

  let boundary = 0;
  let nonManifold = 0;
  for (const [k, count] of forward) {
    const a = Math.floor(k / vertexCount);
    const b = k % vertexCount;
    const opposite = forward.get(key(b, a)) ?? 0;
    if (count > 1) nonManifold++;
    else if (opposite === 0) boundary++;
    else if (opposite > 1) nonManifold++;
  }

  return {
    triangleCount: indices.length / 3,
    vertexCount,
    boundaryEdges: boundary,
    nonManifoldEdges: nonManifold,
    degenerateTriangles: degenerate,
    closed: boundary === 0 && nonManifold === 0,
  };
}

function triangleArea(positions: Float32Array, a: number, b: number, c: number): number {
  const ax = positions[a * 3] as number;
  const ay = positions[a * 3 + 1] as number;
  const az = positions[a * 3 + 2] as number;
  const ux = (positions[b * 3] as number) - ax;
  const uy = (positions[b * 3 + 1] as number) - ay;
  const uz = (positions[b * 3 + 2] as number) - az;
  const vx = (positions[c * 3] as number) - ax;
  const vy = (positions[c * 3 + 1] as number) - ay;
  const vz = (positions[c * 3 + 2] as number) - az;
  const cx = uy * vz - uz * vy;
  const cy = uz * vx - ux * vz;
  const cz = ux * vy - uy * vx;
  return 0.5 * Math.hypot(cx, cy, cz);
}

/** Signed volume of a closed mesh, mm^3. Positive for outward-facing normals. */
export function meshVolume(mesh: ExtractedMesh): number {
  const { positions, indices } = mesh;
  let total = 0;
  for (let t = 0; t < indices.length; t += 3) {
    const a = (indices[t] as number) * 3;
    const b = (indices[t + 1] as number) * 3;
    const c = (indices[t + 2] as number) * 3;
    const ax = positions[a] as number;
    const ay = positions[a + 1] as number;
    const az = positions[a + 2] as number;
    const bx = positions[b] as number;
    const by = positions[b + 1] as number;
    const bz = positions[b + 2] as number;
    const cx = positions[c] as number;
    const cy = positions[c + 1] as number;
    const cz = positions[c + 2] as number;
    total +=
      ax * (by * cz - bz * cy) + ay * (bz * cx - bx * cz) + az * (bx * cy - by * cx);
  }
  return total / 6;
}

export interface ClaySmoothBounds {
  width: number;
  height: number;
  depth: number;
  minX?: number;
  maxX?: number;
  minZ?: number;
  maxZ?: number;
}

/**
 * Shrink-resistant Laplacian polish for the extracted surface.
 *
 * Surface Nets is watertight but its vertices still inherit a faint voxel
 * stair-step along silhouettes. A Taubin pair (positive relax, then a small
 * inflation) removes that pattern without collapsing the hold. Coordinates on
 * the original blank planes stay pinned to those planes, so the rear remains
 * perfectly flat and exported dimensions remain exact.
 */
export function claySmoothMesh(
  mesh: ExtractedMesh,
  bounds: ClaySmoothBounds,
  iterations = 3,
): ExtractedMesh {
  const vertexCount = mesh.positions.length / 3;
  if (vertexCount === 0 || mesh.indices.length === 0 || iterations <= 0) return mesh;

  const degree = new Uint32Array(vertexCount + 1);
  for (let t = 0; t < mesh.indices.length; t += 3) {
    const a = mesh.indices[t] as number;
    const b = mesh.indices[t + 1] as number;
    const c = mesh.indices[t + 2] as number;
    degree[a + 1]! += 2;
    degree[b + 1]! += 2;
    degree[c + 1]! += 2;
  }
  for (let v = 0; v < vertexCount; v++) degree[v + 1]! += degree[v] as number;

  const neighbours = new Uint32Array(mesh.indices.length * 2);
  const cursor = degree.slice(0, vertexCount);
  const link = (a: number, b: number): void => {
    neighbours[cursor[a]!] = b;
    cursor[a]!++;
  };
  for (let t = 0; t < mesh.indices.length; t += 3) {
    const a = mesh.indices[t] as number;
    const b = mesh.indices[t + 1] as number;
    const c = mesh.indices[t + 2] as number;
    link(a, b); link(a, c);
    link(b, a); link(b, c);
    link(c, a); link(c, b);
  }

  const original = mesh.positions;
  let current = new Float32Array(original);
  let scratch = new Float32Array(original.length);
  const epsilon = Math.max(bounds.width, bounds.height, bounds.depth) * 1e-6 + 1e-6;
  const minX = bounds.minX ?? 0;
  const maxX = bounds.maxX ?? bounds.width;
  const minZ = bounds.minZ ?? 0;
  const maxZ = bounds.maxZ ?? bounds.depth;

  const step = (factor: number): void => {
    for (let v = 0; v < vertexCount; v++) {
      const start = degree[v] as number;
      const end = degree[v + 1] as number;
      const p = v * 3;
      if (start === end) {
        scratch[p] = current[p] as number;
        scratch[p + 1] = current[p + 1] as number;
        scratch[p + 2] = current[p + 2] as number;
        continue;
      }

      let x = 0;
      let y = 0;
      let z = 0;
      for (let n = start; n < end; n++) {
        const q = (neighbours[n] as number) * 3;
        x += current[q] as number;
        y += current[q + 1] as number;
        z += current[q + 2] as number;
      }
      const count = end - start;
      x = (current[p] as number) + (x / count - (current[p] as number)) * factor;
      y = (current[p + 1] as number) + (y / count - (current[p + 1] as number)) * factor;
      z = (current[p + 2] as number) + (z / count - (current[p + 2] as number)) * factor;

      const ox = original[p] as number;
      const oy = original[p + 1] as number;
      const oz = original[p + 2] as number;
      if (Math.abs(ox) <= epsilon) x = 0;
      else if (Math.abs(ox - bounds.width) <= epsilon) x = bounds.width;
      if (Math.abs(oy) <= epsilon) y = 0;
      else if (Math.abs(oy - bounds.height) <= epsilon) y = bounds.height;
      if (Math.abs(oz) <= epsilon) z = 0;
      else if (Math.abs(oz - bounds.depth) <= epsilon) z = bounds.depth;

      scratch[p] = clampNumber(x, minX, maxX);
      scratch[p + 1] = clampNumber(y, 0, bounds.height);
      scratch[p + 2] = clampNumber(z, minZ, maxZ);
    }
    const swap = current;
    current = scratch;
    scratch = swap;
  };

  for (let pass = 0; pass < iterations; pass++) {
    step(0.52);
    step(-0.53);
  }

  return polishMountingContour(
    { ...mesh, positions: current },
    bounds,
    Math.max(4, iterations + 1),
  );
}

/**
 * Remove voxel-frequency teeth from the outline where the sculpted side meets
 * the perfectly flat mounting face.
 *
 * A normal surface Laplacian mixes side and rear-face neighbours. At the
 * mounting outline that can leave a faint alternating in/out pattern even
 * though every vertex is correctly pinned to y=0. Here we identify edges
 * shared by one rear-face triangle and one side triangle, then smooth only
 * along that closed contour. Original blank planes stay pinned, so untouched
 * square edges and advertised dimensions remain exact.
 */
export function polishMountingContour(
  mesh: ExtractedMesh,
  bounds: ClaySmoothBounds,
  iterations = 6,
): ExtractedMesh {
  const vertexCount = mesh.positions.length / 3;
  if (vertexCount === 0 || mesh.indices.length === 0 || iterations <= 0) return mesh;

  const epsilon = Math.max(bounds.width, bounds.height, bounds.depth) * 1e-6 + 1e-6;
  const edgeKinds = new Map<number, number>();
  const addEdge = (a: number, b: number, kind: number): void => {
    if (
      Math.abs((mesh.positions[a * 3 + 1] as number)) > epsilon ||
      Math.abs((mesh.positions[b * 3 + 1] as number)) > epsilon
    ) {
      return;
    }
    const lo = Math.min(a, b);
    const hi = Math.max(a, b);
    const key = lo * vertexCount + hi;
    edgeKinds.set(key, (edgeKinds.get(key) ?? 0) | kind);
  };

  for (let t = 0; t < mesh.indices.length; t += 3) {
    const a = mesh.indices[t] as number;
    const b = mesh.indices[t + 1] as number;
    const c = mesh.indices[t + 2] as number;
    const bottom =
      Math.abs(mesh.positions[a * 3 + 1] as number) <= epsilon &&
      Math.abs(mesh.positions[b * 3 + 1] as number) <= epsilon &&
      Math.abs(mesh.positions[c * 3 + 1] as number) <= epsilon;
    const kind = bottom ? 1 : 2;
    addEdge(a, b, kind);
    addEdge(b, c, kind);
    addEdge(c, a, kind);
  }

  const neighbours: number[][] = Array.from({ length: vertexCount }, () => []);
  for (const [key, kind] of edgeKinds) {
    if (kind !== 3) continue;
    const a = Math.floor(key / vertexCount);
    const b = key - a * vertexCount;
    neighbours[a]!.push(b);
    neighbours[b]!.push(a);
  }

  const original = mesh.positions;
  let current = new Float32Array(original);
  let scratch = new Float32Array(original.length);
  const smoothStep = (factor: number): void => {
    scratch.set(current);
    for (let v = 0; v < vertexCount; v++) {
      const adjacent = neighbours[v] as number[];
      if (adjacent.length !== 2) continue;
      const p = v * 3;
      const a = adjacent[0]! * 3;
      const b = adjacent[1]! * 3;
      let x = (current[p] as number) +
        (((current[a] as number) + (current[b] as number)) * 0.5 - (current[p] as number)) * factor;
      let z = (current[p + 2] as number) +
        (((current[a + 2] as number) + (current[b + 2] as number)) * 0.5 -
          (current[p + 2] as number)) * factor;

      const ox = original[p] as number;
      const oz = original[p + 2] as number;
      if (Math.abs(ox) <= epsilon) x = 0;
      else if (Math.abs(ox - bounds.width) <= epsilon) x = bounds.width;
      if (Math.abs(oz) <= epsilon) z = 0;
      else if (Math.abs(oz - bounds.depth) <= epsilon) z = bounds.depth;

      scratch[p] = clampNumber(x, bounds.minX ?? 0, bounds.maxX ?? bounds.width);
      scratch[p + 1] = 0;
      scratch[p + 2] = clampNumber(z, bounds.minZ ?? 0, bounds.maxZ ?? bounds.depth);
    }
    const swap = current;
    current = scratch;
    scratch = swap;
  };

  for (let pass = 0; pass < iterations; pass++) {
    smoothStep(0.42);
    smoothStep(-0.43);
  }

  return { ...mesh, positions: current };
}

function clampNumber(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

export interface ShadedMesh {
  positions: Float32Array;
  normals: Float32Array;
}

/**
 * Per-corner normals with a crease angle: sculpted surfaces read as smooth,
 * while the blank's edges and the rims of fixing holes stay crisp. Output is
 * non-indexed because corners on either side of a crease need different
 * normals.
 */
export function computeCreasedNormals(mesh: ExtractedMesh, creaseDegrees = 82): ShadedMesh {
  const { positions, indices } = mesh;
  const faceCount = indices.length / 3;
  const vertexCount = positions.length / 3;
  const faceNormals = new Float32Array(faceCount * 3);
  const faceWeights = new Float32Array(faceCount * 3);

  for (let f = 0; f < faceCount; f++) {
    const a = (indices[f * 3] as number) * 3;
    const b = (indices[f * 3 + 1] as number) * 3;
    const c = (indices[f * 3 + 2] as number) * 3;
    const ux = (positions[b] as number) - (positions[a] as number);
    const uy = (positions[b + 1] as number) - (positions[a + 1] as number);
    const uz = (positions[b + 2] as number) - (positions[a + 2] as number);
    const vx = (positions[c] as number) - (positions[a] as number);
    const vy = (positions[c + 1] as number) - (positions[a + 1] as number);
    const vz = (positions[c + 2] as number) - (positions[a + 2] as number);
    let nx = uy * vz - uz * vy;
    let ny = uz * vx - ux * vz;
    let nz = ux * vy - uy * vx;
    faceWeights[f * 3] = nx;
    faceWeights[f * 3 + 1] = ny;
    faceWeights[f * 3 + 2] = nz;
    const len = Math.hypot(nx, ny, nz) || 1;
    nx /= len;
    ny /= len;
    nz /= len;
    faceNormals[f * 3] = nx;
    faceNormals[f * 3 + 1] = ny;
    faceNormals[f * 3 + 2] = nz;
  }

  // Flat adjacency: counting sort of faces per vertex.
  const counts = new Uint32Array(vertexCount + 1);
  for (let i = 0; i < indices.length; i++) counts[(indices[i] as number) + 1]!++;
  for (let v = 0; v < vertexCount; v++) counts[v + 1]! += counts[v] as number;
  const adjacency = new Uint32Array(indices.length);
  const cursor = counts.slice(0, vertexCount);
  for (let f = 0; f < faceCount; f++) {
    for (let corner = 0; corner < 3; corner++) {
      const v = indices[f * 3 + corner] as number;
      adjacency[cursor[v]!++] = f;
    }
  }

  const threshold = Math.cos((creaseDegrees * Math.PI) / 180);
  const outPositions = new Float32Array(faceCount * 9);
  const outNormals = new Float32Array(faceCount * 9);

  for (let f = 0; f < faceCount; f++) {
    const fnx = faceNormals[f * 3] as number;
    const fny = faceNormals[f * 3 + 1] as number;
    const fnz = faceNormals[f * 3 + 2] as number;
    for (let corner = 0; corner < 3; corner++) {
      const v = indices[f * 3 + corner] as number;
      let nx = 0;
      let ny = 0;
      let nz = 0;
      const start = counts[v] as number;
      const end = counts[v + 1] as number;
      for (let a = start; a < end; a++) {
        const g = adjacency[a] as number;
        const gx = faceNormals[g * 3] as number;
        const gy = faceNormals[g * 3 + 1] as number;
        const gz = faceNormals[g * 3 + 2] as number;
        if (fnx * gx + fny * gy + fnz * gz < threshold) continue;
        // Area weighting prevents the alternating diagonals of a non-planar
        // Surface Nets quad from showing up as bands on broad cut faces.
        nx += faceWeights[g * 3] as number;
        ny += faceWeights[g * 3 + 1] as number;
        nz += faceWeights[g * 3 + 2] as number;
      }
      const len = Math.hypot(nx, ny, nz);
      const o = f * 9 + corner * 3;
      if (len < 1e-8) {
        outNormals[o] = fnx;
        outNormals[o + 1] = fny;
        outNormals[o + 2] = fnz;
      } else {
        outNormals[o] = nx / len;
        outNormals[o + 1] = ny / len;
        outNormals[o + 2] = nz / len;
      }
      outPositions[o] = positions[v * 3] as number;
      outPositions[o + 1] = positions[v * 3 + 1] as number;
      outPositions[o + 2] = positions[v * 3 + 2] as number;
    }
  }

  return { positions: outPositions, normals: outNormals };
}

/** Concatenate meshes, offsetting indices. */
export function mergeMeshes(meshes: readonly ExtractedMesh[]): ExtractedMesh {
  let vTotal = 0;
  let iTotal = 0;
  for (const m of meshes) {
    vTotal += m.positions.length;
    iTotal += m.indices.length;
  }
  const positions = new Float32Array(vTotal);
  const indices = new Uint32Array(iTotal);
  let vOff = 0;
  let iOff = 0;
  for (const m of meshes) {
    positions.set(m.positions, vOff);
    const base = vOff / 3;
    for (let i = 0; i < m.indices.length; i++) indices[iOff + i] = (m.indices[i] as number) + base;
    vOff += m.positions.length;
    iOff += m.indices.length;
  }
  return { positions, indices };
}
